import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtCurrency } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { GBELogo } from "@/components/layout/GBELogo";
import { Building2, MapPin, Search, Loader2, ArrowLeft, ArrowRight } from "lucide-react";

const HORIZON_STYLE = {
  "Immediate Attention": "bg-rose-100 text-rose-700",
  "12 Month Plan": "bg-amber-100 text-amber-700",
  "24 Month Plan": "bg-blue-100 text-blue-700",
};

function Detail({ label, value }) {
  return (
    <div className="flex justify-between gap-3 py-1.5 border-b border-slate-100 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-900 font-medium text-right">{value ?? "—"}</span>
    </div>
  );
}

export default function ClientViewPage() {
  const { clientId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);

  useEffect(() => {
    api.get(`/site-qr/client-view/${clientId}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Unable to load this client"));
  }, [clientId]);

  const assets = useMemo(() => {
    const list = data?.top_assets || [];
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter((a) =>
      [a.asset_name, a.asset_group, a.location].some((v) => (v || "").toLowerCase().includes(needle)));
  }, [data, q]);

  if (err) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center">
        <div className="rounded-lg px-4 py-3 bg-gbe mb-6"><GBELogo /></div>
        <p className="text-sm text-slate-600" data-testid="client-view-error">{err}</p>
        <Link to="/" className="mt-4 text-sm text-blue-800 underline">Go to the Hub</Link>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-blue-900 animate-spin" />
      </div>
    );
  }

  const { client, summary, sites } = data;
  const mix = summary.condition_mix || {};
  const mixTotal = (mix.good || 0) + (mix.fair || 0) + (mix.poor || 0) || 1;

  return (
    <div className="min-h-screen bg-slate-50 pb-16" data-testid="client-view-page">
      <div className="bg-gbe text-white px-4 pt-5 pb-6">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between">
            <GBELogo />
            <Link to="/" className="text-white/70 text-xs flex items-center gap-1">
              <ArrowLeft className="w-3.5 h-3.5" /> Hub
            </Link>
          </div>
          <div className="mt-4 text-[11px] uppercase tracking-wider text-white/60 flex items-center gap-1">
            <Building2 className="w-3 h-3" /> Client portfolio
          </div>
          <h1 className="font-display text-2xl font-semibold" data-testid="client-view-name">{client.name}</h1>
          <div className="text-white/70 text-xs mt-1">{summary.site_count} site{summary.site_count === 1 ? "" : "s"} · {summary.asset_count} assets</div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 -mt-3 space-y-4">
        <div className="grid grid-cols-3 gap-2">
          {[["Assets", summary.asset_count], ["Immediate", fmtCurrency(summary.budget_immediate)],
            ["12-month plan", fmtCurrency(summary.budget_12m)]].map(([l, v]) => (
            <div key={l} className="bg-white rounded-lg border border-slate-200 p-3 shadow-sm">
              <div className="text-[9px] uppercase tracking-wider text-slate-500">{l}</div>
              <div className="text-base font-display font-semibold text-slate-900 mt-0.5 truncate">{v}</div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Condition mix</div>
          <div className="flex h-2.5 rounded-full overflow-hidden bg-slate-100">
            <div className="bg-emerald-500" style={{ width: `${(100 * (mix.good || 0)) / mixTotal}%` }} />
            <div className="bg-amber-400" style={{ width: `${(100 * (mix.fair || 0)) / mixTotal}%` }} />
            <div className="bg-rose-500" style={{ width: `${(100 * (mix.poor || 0)) / mixTotal}%` }} />
          </div>
          <div className="flex gap-4 mt-2 text-[11px] text-slate-600">
            <span><span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-1" />Good {mix.good || 0}</span>
            <span><span className="inline-block w-2 h-2 rounded-full bg-amber-400 mr-1" />Fair {mix.fair || 0}</span>
            <span><span className="inline-block w-2 h-2 rounded-full bg-rose-500 mr-1" />Poor {mix.poor || 0}</span>
            {mix.unknown > 0 && <span className="text-slate-400">Unrated {mix.unknown}</span>}
          </div>
        </div>

        {/* Site picker */}
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/60 text-[10px] uppercase tracking-wider text-slate-500">
            Sites ({sites.length})
          </div>
          <div className="divide-y divide-slate-100">
            {sites.map((s) => (
              <Link key={s.id} to={`/site/${s.id}`} data-testid={`client-site-${s.id}`}
                    className="flex items-center justify-between gap-2 px-3 py-2.5 hover:bg-slate-50 active:bg-slate-100">
                <div className="min-w-0">
                  <div className="text-sm text-slate-800 font-medium truncate">{s.name}</div>
                  {s.address && (
                    <div className="text-[11px] text-slate-500 truncate flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {s.address}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[11px] text-slate-500">{s.asset_count} assets</span>
                  <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
                </div>
              </Link>
            ))}
            {sites.length === 0 && <div className="p-6 text-center text-sm text-slate-500">No sites recorded for this client.</div>}
          </div>
        </div>

        {/* Top priority assets across the portfolio */}
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 bg-slate-50/60">
            <Search className="w-3.5 h-3.5 text-slate-400" />
            <input data-testid="client-view-search" value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search top priority assets…"
                   className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400" />
            <span className="text-[10px] text-slate-400 whitespace-nowrap">{assets.length}</span>
          </div>
          <div className="divide-y divide-slate-100">
            {assets.map((a) => (
              <button key={a.id} data-testid={`client-asset-${a.id}`} onClick={() => setSel(a)}
                      className="w-full text-left px-3 py-2.5 hover:bg-slate-50 active:bg-slate-100">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm text-slate-800 font-medium truncate">{a.asset_name || "—"}</div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {a.asset_group || "—"}{a.location ? ` · ${a.location}` : ""}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[11px] text-slate-500">Cond <span className="font-semibold text-slate-800">{a.condition ?? "—"}</span></div>
                    <div className="text-[11px] text-slate-500">Renew <span className="font-semibold text-slate-800">{a.renewal_year || "—"}</span></div>
                  </div>
                </div>
                {a.horizon && a.horizon !== "Monitor" && (
                  <Badge className={`mt-1 text-[9px] ${HORIZON_STYLE[a.horizon] || "bg-slate-100 text-slate-600"} hover:bg-inherit`}>
                    {a.horizon === "Immediate Attention" ? "Beyond design life" : a.horizon}
                  </Badge>
                )}
              </button>
            ))}
            {assets.length === 0 && <div className="p-6 text-center text-sm text-slate-500">No assets found.</div>}
          </div>
        </div>
      </div>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-md" data-testid="client-asset-detail">
          <DialogHeader>
            <DialogTitle className="text-base">{sel?.asset_name}</DialogTitle>
          </DialogHeader>
          {sel && (
            <div>
              <Detail label="Class" value={sel.asset_group} />
              <Detail label="Type" value={sel.asset_type} />
              <Detail label="Location" value={sel.location} />
              <Detail label="Condition (1 best – 5 worst)" value={sel.condition} />
              <Detail label="Health score" value={sel.health_score != null ? Math.round(sel.health_score) : null} />
              <Detail label="Priority score" value={sel.priority != null ? Math.round(sel.priority) : null} />
              <Detail label="Installed" value={sel.install_year} />
              <Detail label="Remaining life (yrs)" value={sel.remaining_life_years != null ? Math.round(sel.remaining_life_years) : null} />
              <Detail label="Forecast renewal" value={sel.renewal_year} />
              <Detail label="Recommended action" value={sel.recommended_action} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
